import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { carouselAPI, statsAPI, trafficAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import { Shield, Clock, MapPin, Phone, Mail, Star, Users, Plane, Navigation, ChevronRight, AlertTriangle } from 'lucide-react';

// Thirugoo Logo Component
const ThirugooLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="/thirugoo-logo.png" 
    alt="Thirugoo" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [carouselImages, setCarouselImages] = useState([]);
  const [trafficUpdate, setTrafficUpdate] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, carouselRes, trafficRes] = await Promise.all([
        statsAPI.getDashboard(),
        carouselAPI.getAll(),
        trafficAPI.get()
      ]);
      setStats(statsRes.data);
      setCarouselImages(carouselRes.data);
      if (trafficRes.data && trafficRes.data.message) {
        setTrafficUpdate(trafficRes.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleGetStarted = () => {
    if (user) {
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'driver') navigate('/driver');
      else navigate('/customer');
    } else {
      navigate('/login');
    }
  };

  const defaultImages = [
    { id: 1, title: 'Safe & Reliable', description: 'Your trusted local cab service', color: 'from-amber-500 to-orange-600' },
    { id: 2, title: '24/7 Service', description: 'Available round the clock', color: 'from-blue-500 to-indigo-600' },
    { id: 3, title: 'Best Rates', description: 'Transparent and fair pricing', color: 'from-green-500 to-teal-600' },
  ];

  const displayImages = carouselImages.length > 0 ? carouselImages : defaultImages;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ThirugooLogo className="h-10 w-10" />
            <span className="text-2xl font-bold text-gray-900">Thirugoo</span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#home" className="text-gray-600 hover:text-amber-600 transition">Home</a>
            <a href="#services" className="text-gray-600 hover:text-amber-600 transition">Services</a>
            <a href="#about" className="text-gray-600 hover:text-amber-600 transition">About</a>
            <a href="#contact" className="text-gray-600 hover:text-amber-600 transition">Contact</a>
          </nav>
          <Button onClick={handleGetStarted} className="bg-amber-600 hover:bg-amber-700">
            {user ? 'Go to Dashboard' : 'Book Now'}
          </Button>
        </div>
      </header>

      {/* Hero Carousel */}
      <section id="home" className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <Carousel className="w-full">
            <CarouselContent>
              {displayImages.map((item, index) => (
                <CarouselItem key={item.id || index}>
                  <Card className="border-0 shadow-xl overflow-hidden">
                    <CardContent className="p-0">
                      {item.image_base64 ? (
                        <div className="relative h-64 md:h-96">
                          <img
                            src={`data:image/jpeg;base64,${item.image_base64}`}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <div className="text-center text-white">
                              <h2 className="text-3xl md:text-5xl font-bold mb-2">{item.title}</h2>
                              <p className="text-lg md:text-xl">{item.description}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={`h-64 md:h-96 bg-gradient-to-r ${item.color} flex items-center justify-center`}>
                          <div className="text-center text-white px-4">
                            <h2 className="text-3xl md:text-5xl font-bold mb-4">{item.title}</h2>
                            <p className="text-lg md:text-xl mb-6">{item.description}</p>
                            <Button onClick={handleGetStarted} size="lg" className="bg-white text-gray-900 hover:bg-gray-100">
                              Book Your Ride <ChevronRight className="ml-2 h-5 w-5" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-4" />
            <CarouselNext className="right-4" />
          </Carousel>
        </div>
      </section>

      {/* Why Choose Thirugoo */}
      <section id="services" className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">Why Choose Thirugoo?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-amber-500">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500 transition-colors">
                  <ThirugooLogo className="h-10 w-10" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Thirugoo Fleet</h3>
                <p className="text-gray-600">Well-maintained vehicles for a comfortable journey. From hatchbacks to luxury sedans.</p>
              </CardContent>
            </Card>
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-amber-500">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500 transition-colors">
                  <Star className="h-8 w-8 text-amber-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Thirugoo Luxe</h3>
                <p className="text-gray-600">Premium travel experience with luxury vehicles and professional chauffeurs.</p>
              </CardContent>
            </Card>
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-amber-500">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500 transition-colors">
                  <Clock className="h-8 w-8 text-amber-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold mb-2">24/7 Service</h3>
                <p className="text-gray-600">Available round the clock. Book anytime, travel anytime.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-90" />
              <div className="text-4xl font-bold mb-1">{stats?.total_customers || 1000}+</div>
              <div className="text-amber-100">Trusted Residents</div>
            </div>
            <div className="text-center">
              <Plane className="h-10 w-10 mx-auto mb-3 opacity-90" />
              <div className="text-4xl font-bold mb-1">{stats?.daily_airport_pickups || 20}</div>
              <div className="text-amber-100">Daily Airport Pickups</div>
            </div>
            <div className="text-center">
              <Navigation className="h-10 w-10 mx-auto mb-3 opacity-90" />
              <div className="text-4xl font-bold mb-1">{stats?.total_distance_km || 25000}+</div>
              <div className="text-amber-100">KM Travelled in 2026</div>
            </div>
            <div className="text-center">
              <Shield className="h-10 w-10 mx-auto mb-3 opacity-90" />
              <div className="text-4xl font-bold mb-1">100%</div>
              <div className="text-amber-100">Safe Rides</div>
            </div>
          </div>
        </div>
      </section>

      {/* Download App CTA */}
      <section className="py-16 px-4 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Download Thirugoo App</h2>
          <p className="text-gray-400 mb-8 text-lg">Book rides on the go with our mobile-friendly platform</p>
          <Button onClick={handleGetStarted} size="lg" className="bg-amber-600 hover:bg-amber-700 text-lg px-8 py-6">
            <ThirugooLogo className="mr-2 h-6 w-6" /> Book Your Ride Now
          </Button>
        </div>
      </section>

      {/* Help Section */}
      <section id="about" className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">Help & Support</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* About Us */}
            <Card className="h-full">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold mb-4 text-amber-600">About Us</h3>
                <p className="text-gray-600 leading-relaxed">
                  Your local driver serving for the last 10 years in your community alone. 
                  Thirugoo is committed to providing safe, reliable, and affordable transportation 
                  services to our community. We take pride in our professional drivers and 
                  well-maintained vehicles.
                </p>
              </CardContent>
            </Card>

            {/* Contact Us */}
            <Card id="contact" className="h-full">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold mb-4 text-amber-600">Contact Us</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-amber-600" />
                    <a href="tel:9164212758" className="text-gray-600 hover:text-amber-600">9164212758</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-amber-600" />
                    <a href="mailto:thirumalesh@thirugoo.in" className="text-gray-600 hover:text-amber-600">thirumalesh@thirugoo.in</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-amber-600" />
                    <span className="text-gray-600">Our Chikkatirupathi, Malur</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Code of Conduct */}
      <section className="py-16 px-4 bg-amber-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">Our Code of Conduct</h2>
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2 text-amber-700">Professionalism & Appearance</h4>
                <p className="text-gray-600">Drivers must be well-groomed and maintain a clean, odour-free vehicle at all times. Smoking or chewing tobacco inside the vehicle is strictly prohibited.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2 text-amber-700">Punctuality & Reliability</h4>
                <p className="text-gray-600">Be at the pickup location 5 minutes before the scheduled time. In case of delays due to local traffic or roadblocks, the customer must be informed immediately.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2 text-amber-700">Safety First</h4>
                <p className="text-gray-600">Follow all traffic rules. Never exceed speed limits, especially in village zones or near schools. Use of mobile phones while driving (except for navigation) is prohibited.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2 text-amber-700">Customer Interaction</h4>
                <p className="text-gray-600">Greet customers with "Namaskara." Help elderly passengers or those with heavy luggage. Avoid personal questions or political/religious discussions.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2 text-amber-700">Transparent Pricing</h4>
                <p className="text-gray-600">Only charge the fare shown in the app. Asking for "extra" or "waiting charges" beyond the agreed terms is a violation of trust.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Traffic Updates Ribbon */}
      {trafficUpdate && trafficUpdate.message && trafficUpdate.is_active && (
        <div className="bg-red-600 text-white py-3 overflow-hidden">
          <div className="animate-marquee whitespace-nowrap flex items-center">
            <span className="mx-4 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <strong>Important Traffic Updates:</strong>
            </span>
            <span className="mx-4">{trafficUpdate.message}</span>
            <span className="mx-4 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <strong>Important Traffic Updates:</strong>
            </span>
            <span className="mx-4">{trafficUpdate.message}</span>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <ThirugooLogo className="h-8 w-8" />
                <span className="text-xl font-bold">Thirugoo</span>
              </div>
              <p className="text-gray-400">Your trusted local cab service since 2015.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#home" className="hover:text-amber-500 transition">Home</a></li>
                <li><a href="#services" className="hover:text-amber-500 transition">Services</a></li>
                <li><a href="#about" className="hover:text-amber-500 transition">About Us</a></li>
                <li><a href="#contact" className="hover:text-amber-500 transition">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Follow Us</h4>
              <div className="flex gap-4">
                <a href="#" className="text-gray-400 hover:text-amber-500 transition">Facebook</a>
                <a href="#" className="text-gray-400 hover:text-amber-500 transition">Instagram</a>
                <a href="#" className="text-gray-400 hover:text-amber-500 transition">YouTube</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2026 Thirugoo. All rights reserved. | thirugoo.biz</p>
          </div>
        </div>
      </footer>

      {/* Marquee Animation Style */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Landing;
